import { useNavigate, useSearchParams } from '@solidjs/router'
import QRCode from 'qrcode'
import { For, Show, createEffect, createSignal, onMount } from 'solid-js'
import HubView from '../components/hub/HubView'
import { createCheckIn, createTopUp, getVenue, getWalletPass, listVenues } from '../lib/api'
import type { VenueSummary } from '../lib/api'
import { useAuth } from '../lib/auth'
import { isDemoMode } from '../lib/demo'
import {
  enqueueCheckIn,
  ensurePersistentStorage,
  isOnline,
  newQueueId,
  readQueue,
  registerBackgroundSync,
  removeFromQueue,
} from '../lib/pwa'
import { filterVenues } from '../lib/venues'

type View = 'pass' | 'topup' | 'checkin' | 'confirm'

export default function Pass() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const auth = useAuth()

  const [view, setView] = createSignal<View>('pass')
  const [topUpAmount, setTopUpAmount] = createSignal(500) // €5.00 in cents
  const [isProcessing, setIsProcessing] = createSignal(false)
  /** Set when a top-up credited instantly (virtual money) — drives the confirmation. */
  const [topUpDone, setTopUpDone] = createSignal(false)
  const [walletPass, setWalletPass] = createSignal<{
    passId: string
    addToWalletUrl: string
    balance: number
    itemsCheckedIn: number
  } | null>(null)
  const [venueSlug, setVenueSlug] = createSignal('')
  const [venue, setVenue] = createSignal<any>(null)
  // The venue directory — the real, discoverable way to pick a venue. Loaded
  // live from GET /v1/venues; nothing about the venue is hard-coded.
  const [venues, setVenues] = createSignal<VenueSummary[]>([])
  const [venuesLoaded, setVenuesLoaded] = createSignal(false)
  const [venuesError, setVenuesError] = createSignal<string | null>(null)
  const [venueQuery, setVenueQuery] = createSignal('')
  const [venueError, setVenueError] = createSignal<string | null>(null)
  const [selectedItems, setSelectedItems] = createSignal<Array<{ type: string; price: number }>>([])
  const [checkInResult, setCheckInResult] = createSignal<any>(null)
  const [applyVoucher, setApplyVoucher] = createSignal(false)
  const [qrImage, setQrImage] = createSignal('')
  const [offlineSaved, setOfflineSaved] = createSignal(false)
  const [queuedCount, setQueuedCount] = createSignal(readQueue().length)

  // The check-in QR is what the counter scans, so render it for real (the app
  // already ships `qrcode`); the payload is also shown as text so it can be typed
  // in by hand when a camera is not at hand.
  createEffect(async () => {
    const payload = checkInResult()?.qrPayload
    if (!payload) {
      setQrImage('')
      return
    }
    try {
      setQrImage(await QRCode.toDataURL(String(payload), { width: 384, margin: 1 }))
    } catch {
      setQrImage('')
    }
  })

  // Opening the check-in flow without a venue means the guest is looking for
  // one — fetch the public directory lazily, once per visit.
  createEffect(() => {
    if (view() === 'checkin' && !venue() && !venuesLoaded()) void loadVenues()
  })

  onMount(async () => {
    if (!auth.isAuthenticated()) {
      navigate('/signup')
      return
    }
    await loadPass()
    // QR entry path: a venue QR code lands the guest on /signup?venue=<slug>,
    // and signup forwards it here — pre-select that venue so check-in is one
    // tap from the door.
    const fromQr = Array.isArray(searchParams.venue) ? searchParams.venue[0] : searchParams.venue
    if (fromQr) {
      setVenueSlug(fromQr)
      await selectVenue(fromQr)
    }
    // Keep the offline queue safe from eviction, and ask for a background sync so
    // a queued check-in goes out as soon as there is signal.
    void ensurePersistentStorage()
    void registerBackgroundSync()
    // Anything queued while offline goes out as soon as we have signal again.
    setQueuedCount(readQueue().length)
    void flushQueue()
    window.addEventListener('online', () => {
      void flushQueue()
    })
  })

  async function loadPass() {
    const res = await getWalletPass()
    if (res.data) {
      setWalletPass(res.data)
    }
  }

  async function handleTopUp() {
    setIsProcessing(true)
    const res = await createTopUp(topUpAmount())
    if (res.data) {
      // DEMO SURFACE: the server credits virtual money instantly and returns
      // `credited: true` with no checkoutUrl. There is nothing to redirect to —
      // refresh the balance so the guest sees the money immediately.
      // PRODUCTION: a Stripe Checkout URL comes back and we hand the guest over.
      if (res.data.credited) {
        await auth.refreshProfile()
        await loadPass()
        setTopUpDone(true)
      } else if (res.data.checkoutUrl) {
        window.location.href = res.data.checkoutUrl
      }
    }
    setIsProcessing(false)
  }

  /** Fetch the public venue directory (GET /v1/venues) — once per visit. */
  async function loadVenues() {
    if (venuesLoaded()) return
    const res = await listVenues()
    if (res.data) {
      setVenues(res.data)
      setVenuesLoaded(true)
    } else {
      setVenuesError(
        res.error?.message ?? 'Could not load the venue list. Check your connection, or enter a code below.',
      )
    }
  }

  /** Look up one venue by slug or id and open its check-in flow. */
  async function selectVenue(slug: string) {
    setIsProcessing(true)
    setVenueError(null)
    const res = await getVenue(slug)
    if (res.data) {
      setVenue(res.data)
      setVenueSlug(res.data.slug ?? slug)
      setSelectedItems([{ type: res.data.itemTypes?.[0]?.name || 'Item', price: res.data.itemTypes?.[0]?.price || 99 }])
      setView('checkin')
    } else {
      setVenueError(res.error?.message ?? 'Venue not found — check the code and try again.')
    }
    setIsProcessing(false)
  }

  async function handleScanVenue() {
    if (!venueSlug()) return
    await selectVenue(venueSlug())
  }

  function addItem() {
    const v = venue()
    if (!v) return
    const itemType = v.itemTypes?.[0] || { name: 'Item', price: 99 }
    setSelectedItems([...selectedItems(), { type: itemType.name, price: itemType.price }])
  }

  function removeItem(index: number) {
    setSelectedItems(selectedItems().filter((_, i) => i !== index))
  }

  async function handleCheckIn() {
    setIsProcessing(true)
    setOfflineSaved(false)
    const v = venue()
    if (!v) {
      setIsProcessing(false)
      return
    }

    const items = selectedItems().map((i) => ({ type: i.name || 'item' }))

    // Progressive enhancement: the wardrobe may have no signal. Queue the intent
    // instead of losing it, and replay it when connectivity returns.
    if (!isOnline()) {
      const queued = enqueueCheckIn({
        id: newQueueId(),
        venueId: v.id ?? v.slug ?? venueSlug(),
        items,
        createdAt: new Date().toISOString(),
      })
      setQueuedCount(queued)
      setOfflineSaved(true)
      setIsProcessing(false)
      return
    }

    const res = await createCheckIn(v.id ?? v.slug ?? venueSlug(), items, { applyVoucher: applyVoucher() })
    if (res.data) {
      setCheckInResult(res.data)
      setView('confirm')
      await auth.refreshProfile()
      await loadPass()
    } else if (!res.error) {
      // No payload and no API error means the request never reached the server.
      const queued = enqueueCheckIn({
        id: newQueueId(),
        venueId: v.id ?? v.slug ?? venueSlug(),
        items,
        createdAt: new Date().toISOString(),
      })
      setQueuedCount(queued)
      setOfflineSaved(true)
    }
    setIsProcessing(false)
  }

  /** Replays check-ins that were started while offline. */
  async function flushQueue() {
    const queue = readQueue()
    if (queue.length === 0) return
    for (const entry of queue) {
      const res = await createCheckIn(entry.venueId, entry.items, entry.guestId)
      if (res.data) removeFromQueue(entry.id)
    }
    setQueuedCount(readQueue().length)
  }

  const balance = () => auth.user()?.balance ?? walletPass()?.balance ?? 0
  const totalCheckIn = () => selectedItems().reduce((sum, item) => sum + item.price, 0)
  const hasEnoughBalance = () => balance() >= totalCheckIn()

  return (
    <div class="min-h-dvh bg-gray-50 flex flex-col">
      {/* Header */}
      <header class="bg-white border-b border-gray-200 px-4 py-3">
        <div class="max-w-md mx-auto flex items-center justify-between">
          <div class="flex items-center gap-2">
            <img src="/logo.svg" alt="ZipCheck" class="h-6" />
            <span class="font-semibold text-gray-900">ZipCheck</span>
          </div>
          <button onClick={() => auth.logout()} class="text-sm text-gray-500 hover:text-gray-700">
            Sign out
          </button>
        </div>
      </header>

      <main class="flex-1 p-4">
        <div class="max-w-md mx-auto">
          {/* Pass View — the hub. All hub presentation lives in components/hub;
              the flows (topup/checkin/confirm) stay here. */}
          <Show when={view() === 'pass'}>
            <HubView
              onTopUp={() => setView('topup')}
              onCheckIn={() => setView('checkin')}
              onSignOut={() => auth.logout()}
              offlineSaved={offlineSaved()}
            />
          </Show>

          {/* Top Up View */}
          <Show when={view() === 'topup'}>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <button onClick={() => setView('pass')} class="text-sm text-gray-500 hover:text-gray-700 mb-4">
                ← Back
              </button>

              <h1 class="text-xl font-bold text-gray-900 mb-2">Top up your balance</h1>
              <p class="text-sm text-gray-600 mb-6">Load credits to check in instantly at any venue.</p>

              <div class="grid grid-cols-3 gap-3 mb-6">
                <For each={[500, 1000, 2000, 5000, 10000, 20000]}>
                  {(amount) => (
                    <button
                      onClick={() => setTopUpAmount(amount)}
                      class={`py-3 px-4 rounded-lg text-sm font-semibold transition-colors
                        ${
                          topUpAmount() === amount
                            ? 'bg-emerald-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      €{(amount / 100).toFixed(0)}
                    </button>
                  )}
                </For>
              </div>

              <div class="mb-6 p-4 bg-gray-50 rounded-lg">
                <div class="flex justify-between text-sm">
                  <span class="text-gray-500">Current balance</span>
                  <span class="font-medium">€{(balance() / 100).toFixed(2)}</span>
                </div>
                <div class="flex justify-between text-sm mt-1">
                  <span class="text-gray-500">Loading</span>
                  <span class="font-medium text-emerald-600">+€{(topUpAmount() / 100).toFixed(2)}</span>
                </div>
                <div class="border-t border-gray-200 mt-2 pt-2 flex justify-between text-sm">
                  <span class="font-medium text-gray-700">New balance</span>
                  <span class="font-bold">€{((balance() + topUpAmount()) / 100).toFixed(2)}</span>
                </div>
              </div>

              <button
                onClick={handleTopUp}
                disabled={isProcessing()}
                class="w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg
                       hover:bg-emerald-700 active:bg-emerald-800
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-colors"
              >
                {isProcessing() ? 'Processing...' : `Load €${(topUpAmount() / 100).toFixed(2)}`}
              </button>

              <Show when={topUpDone()}>
                <p class="mt-3 text-sm text-emerald-700 font-medium text-center">
                  €{(topUpAmount() / 100).toFixed(2)} added to your balance.
                </p>
              </Show>

              {/* The demo credits virtual money with no card and no redirect, so the
                  "Powered by Stripe" claim would be untrue there. */}
              <Show
                when={!isDemoMode()}
                fallback={
                  <p class="mt-3 text-xs text-gray-500 text-center">Demo mode — virtual balance, no card needed.</p>
                }
              >
                <p class="mt-3 text-xs text-gray-500 text-center">
                  Powered by Stripe. Card, Apple Pay, Google Pay accepted.
                </p>
              </Show>
            </div>
          </Show>

          {/* Check In View */}
          <Show when={view() === 'checkin'}>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <button
                onClick={() => {
                  setView('pass')
                  setVenue(null)
                  setSelectedItems([])
                }}
                class="text-sm text-gray-500 hover:text-gray-700 mb-4"
              >
                ← Back
              </button>

              <Show
                when={venue()}
                fallback={
                  <>
                    <h1 class="text-xl font-bold text-gray-900 mb-2">Check in items</h1>

                    {/* PRE-ORDER stage: apply the guest's voucher (referral reward) to this
                  check-in. The API decides the percentage; nothing is promised here. */}
                    <label class="flex items-start gap-2 mb-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <input
                        type="checkbox"
                        checked={applyVoucher()}
                        onChange={(e) => setApplyVoucher(e.currentTarget.checked)}
                        class="mt-0.5 h-4 w-4 accent-emerald-600"
                      />
                      <span class="text-sm text-emerald-900">
                        Pre-order with my voucher
                        <span class="block text-[11px] text-emerald-700/80">
                          Uses your referral voucher if one is active — the discount is applied at the venue.
                        </span>
                      </span>
                    </label>

                    {/* CHOOSE ITEMS? -> NO: BROWSE ITEMS ONLY — the venue catalogue, read-only */}
                    <details class="mb-3 rounded-lg border border-gray-200 bg-white p-3">
                      <summary class="text-sm font-medium text-gray-700 cursor-pointer">
                        Just browsing? See what this venue checks in
                      </summary>
                      <ul class="mt-2 space-y-1">
                        <For each={venue()?.itemTypes ?? []}>
                          {(it: { name: string; price: number }) => (
                            <li class="flex items-center justify-between text-sm text-gray-600">
                              <span>{it.name}</span>
                              <span>€{(it.price / 100).toFixed(2)}</span>
                            </li>
                          )}
                        </For>
                      </ul>
                    </details>
                    <p class="text-sm text-gray-600 mb-3">Find the venue you're at — search by name or street.</p>

                    <input
                      type="search"
                      placeholder="Search venues"
                      value={venueQuery()}
                      onInput={(e) => setVenueQuery(e.currentTarget.value)}
                      class="w-full px-4 py-3 border border-gray-300 rounded-lg text-base mb-3
                             focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />

                    <Show when={venuesError()}>
                      <p class="text-sm text-red-600 mb-3">{venuesError()}</p>
                    </Show>

                    <Show when={!venuesLoaded() && !venuesError()}>
                      <p class="text-sm text-gray-400 mb-3">Loading venues…</p>
                    </Show>

                    {/* The real venue directory (GET /v1/venues): the guest picks
                        from live data — no hard-coded slug anywhere. */}
                    <div class="space-y-2 mb-4">
                      <For each={filterVenues(venues(), venueQuery())}>
                        {(v) => (
                          <button
                            onClick={() => void selectVenue(v.slug)}
                            disabled={isProcessing()}
                            class="w-full text-left p-3 bg-gray-50 hover:bg-emerald-50 border border-gray-200
                                   rounded-lg transition-colors disabled:opacity-50"
                          >
                            <p class="text-sm font-medium text-gray-900">{v.name}</p>
                            <Show when={v.address}>
                              <p class="text-xs text-gray-500">{v.address}</p>
                            </Show>
                          </button>
                        )}
                      </For>
                      <Show when={venuesLoaded() && filterVenues(venues(), venueQuery()).length === 0}>
                        <p class="text-sm text-gray-500">No venue matches that search — enter the code instead.</p>
                      </Show>
                    </div>

                    {/* Fallback for a venue not in the list yet: the code printed
                        on the counter sign still works. */}
                    <details class="rounded-lg border border-gray-200 bg-white p-3">
                      <summary class="text-sm font-medium text-gray-700 cursor-pointer">
                        Enter a venue code instead
                      </summary>
                      <div class="flex gap-2 mt-3">
                        <input
                          type="text"
                          placeholder="Venue code"
                          value={venueSlug()}
                          onInput={(e) => setVenueSlug(e.currentTarget.value)}
                          class="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-base
                                 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        />
                        <button
                          onClick={handleScanVenue}
                          disabled={isProcessing() || !venueSlug()}
                          class="py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg
                                 hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                        >
                          {isProcessing() ? '...' : 'Go'}
                        </button>
                      </div>
                      <Show when={venueError()}>
                        <p class="mt-2 text-sm text-red-600">{venueError()}</p>
                      </Show>
                    </details>
                  </>
                }
              >
                <h1 class="text-xl font-bold text-gray-900 mb-1">{venue()!.name}</h1>
                <p class="text-sm text-gray-500 mb-4">{venue()!.address}</p>

                <h2 class="text-sm font-semibold text-gray-700 mb-2">Items to check in</h2>

                <div class="space-y-2 mb-4">
                  <For each={selectedItems()}>
                    {(item, index) => (
                      <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p class="text-sm font-medium text-gray-900">{item.type}</p>
                          <p class="text-xs text-gray-500">€{(item.price / 100).toFixed(2)}</p>
                        </div>
                        <button onClick={() => removeItem(index())} class="text-sm text-red-500 hover:text-red-700">
                          Remove
                        </button>
                      </div>
                    )}
                  </For>
                </div>

                <button
                  onClick={addItem}
                  class="w-full py-2 px-4 bg-gray-100 text-gray-700 rounded-lg text-sm
                         hover:bg-gray-200 transition-colors mb-4"
                >
                  + Add item
                </button>

                <div class="border-t border-gray-200 pt-4 mb-4">
                  <div class="flex justify-between text-sm">
                    <span class="text-gray-500">Total</span>
                    <span class="font-bold text-lg">€{(totalCheckIn() / 100).toFixed(2)}</span>
                  </div>
                  <div class="flex justify-between text-sm mt-1">
                    <span class="text-gray-500">Your balance</span>
                    <span class={hasEnoughBalance() ? 'text-emerald-600' : 'text-red-600'}>
                      €{(balance() / 100).toFixed(2)}
                    </span>
                  </div>
                </div>

                <Show when={!hasEnoughBalance()}>
                  <div class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    Not enough balance.{' '}
                    <button onClick={() => setView('topup')} class="font-semibold underline">
                      Top up now
                    </button>
                  </div>
                </Show>

                <button
                  onClick={handleCheckIn}
                  disabled={isProcessing() || !hasEnoughBalance() || selectedItems().length === 0}
                  class="w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg
                         hover:bg-emerald-700 active:bg-emerald-800
                         disabled:opacity-50 disabled:cursor-not-allowed
                         transition-colors"
                >
                  {isProcessing()
                    ? 'Processing...'
                    : `Check in ${selectedItems().length} item${selectedItems().length !== 1 ? 's' : ''}`}
                </button>
              </Show>
            </div>
          </Show>

          {/* Confirmation View */}
          <Show when={view() === 'confirm'}>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
              <div class="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span class="text-3xl">✓</span>
              </div>

              <h1 class="text-xl font-bold text-gray-900 mb-2">Checked in!</h1>
              <Show when={checkInResult()?.discountPct}>
                <p class="text-xs text-emerald-700 mb-1">
                  Voucher applied: −{checkInResult()?.discountPct}% (was €
                  {((checkInResult()?.subtotal || 0) / 100).toFixed(2)})
                </p>
              </Show>
              <p class="text-sm text-gray-600 mb-4">
                {checkInResult()?.items?.length || 0} item{(checkInResult()?.items?.length || 0) !== 1 ? 's' : ''}{' '}
                checked in at {venue()?.name}
              </p>

              <div class="p-4 bg-gray-50 rounded-lg mb-6">
                <Show when={checkInResult()?.qrPayload}>
                  <div class="mb-3">
                    <p class="text-xs text-gray-500 mb-2">Show this QR to staff</p>
                    <Show when={qrImage()} fallback={<p class="text-xs text-gray-400">Generating QR…</p>}>
                      <img src={qrImage()} alt="Check-in QR code" class="w-48 h-48 mx-auto" />
                    </Show>
                    <p class="text-[10px] text-gray-400 mt-2 break-all">{checkInResult()?.qrPayload}</p>
                  </div>
                </Show>

                <div class="flex justify-between text-sm">
                  <span class="text-gray-500">Balance deducted</span>
                  <span class="font-medium">€{(checkInResult()?.totalAmount / 100).toFixed(2)}</span>
                </div>
                <div class="flex justify-between text-sm mt-1">
                  <span class="text-gray-500">Remaining balance</span>
                  <span class="font-bold">€{(checkInResult()?.balanceAfter / 100).toFixed(2)}</span>
                </div>
              </div>

              <button
                onClick={() => {
                  setView('pass')
                  setVenue(null)
                  setSelectedItems([])
                  setCheckInResult(null)
                }}
                class="w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg
                       hover:bg-emerald-700 transition-colors"
              >
                Done
              </button>
            </div>
          </Show>
        </div>
      </main>
    </div>
  )
}
